import { useRef } from 'react';
import { Trophy } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { generateFightOdds, formatOdds } from "@/lib/bettingUtils";
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import type { FightCard, Bout } from "@/types/fightCard";
import type { Boxer } from "@/types/boxer";

interface FightCardDisplayProps {
  fightCard: FightCard;
  boxers?: Boxer[];
}

export function FightCardDisplay({ fightCard, boxers = [], compact = false }: FightCardDisplayProps & { compact?: boolean }) {
  const cardRef = useRef<HTMLDivElement>(null);
  
  const sponsors = fightCard.sponsors
    .split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0);

  const handleContextMenu = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!cardRef.current) return;
    
    try {
      // Get the actual scrollHeight to capture full content
      const node = cardRef.current;
      const originalOverflow = node.style.overflow;
      const originalMaxHeight = node.style.maxHeight;
      
      // Temporarily remove any height/scroll constraints
      node.style.overflow = 'visible';
      node.style.maxHeight = 'none';
      
      const dataUrl = await toPng(node, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: '#1a1a1a',
        width: node.scrollWidth,
        height: node.scrollHeight,
        style: {
          transform: 'none',
          maxHeight: 'none',
          overflow: 'visible',
        }
      });
      
      // Restore original styles
      node.style.overflow = originalOverflow;
      node.style.maxHeight = originalMaxHeight;
      
      const link = document.createElement('a');
      link.download = `fight-card-${fightCard.id}.png`;
      link.href = dataUrl;
      link.click();
      
      toast.success('Fight card saved as image!');
    } catch (error) {
      console.error('Failed to save image:', error);
      toast.error('Failed to save image');
    }
  };

  const getBoxerById = (id?: string) => {
    if (!id) return null;
    return boxers.find(b => b.id === id);
  };

  // Calculate responsive font size based on name length
  const getFighterNameSize = (name1: string = '', name2: string = '', isMainEvent: boolean, isCoMain: boolean, compact: boolean) => {
    const maxLength = Math.max(name1.length, name2.length);
    
    if (compact) {
      if (maxLength > 15) return isMainEvent ? 'text-base' : 'text-sm';
      if (maxLength > 12) return isMainEvent ? 'text-lg' : 'text-base';
      return isMainEvent ? 'text-xl' : isCoMain ? 'text-lg' : 'text-base';
    }
    
    // Full size responsive
    if (maxLength > 18) return isMainEvent ? 'text-3xl md:text-4xl' : isCoMain ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl';
    if (maxLength > 15) return isMainEvent ? 'text-4xl md:text-5xl' : isCoMain ? 'text-3xl md:text-4xl' : 'text-2xl md:text-3xl';
    if (maxLength > 12) return isMainEvent ? 'text-5xl md:text-6xl' : isCoMain ? 'text-4xl md:text-5xl' : 'text-3xl md:text-4xl';
    if (maxLength > 10) return isMainEvent ? 'text-6xl md:text-7xl' : isCoMain ? 'text-5xl md:text-6xl' : 'text-4xl md:text-5xl';
    return isMainEvent ? 'text-7xl md:text-9xl' : isCoMain ? 'text-6xl md:text-7xl' : 'text-5xl md:text-6xl';
  };

  const renderBout = (bout: Bout, showBadge: boolean = false, badgeText?: string) => {
    if (!bout.fighter1 && !bout.fighter2) return null;

    const hasTitle = bout.title && bout.title.trim().length > 0;
    const isMainEvent = bout.type === 'main';
    const isCoMain = bout.type === 'co-main';
    const showImages = (isMainEvent || isCoMain) && (bout.fighter1Image || bout.fighter2Image);
    
    // Get responsive font size for both fighters
    const nameSize = getFighterNameSize(bout.fighter1 || '', bout.fighter2 || '', isMainEvent, isCoMain, compact);

    // Calculate odds if both fighters exist
    const fighter1 = getBoxerById(bout.fighter1Id);
    const fighter2 = getBoxerById(bout.fighter2Id);
    let odds = null;
    if (fighter1 && fighter2) {
      odds = generateFightOdds(
        bout.id,
        fighter1,
        fighter2,
        boxers,
        undefined,
        'american'
      );
    }

    return (
      <div className={`flex flex-col items-center ${compact ? 'gap-1.5' : 'gap-3'}`}>
        {showBadge && badgeText && (
          <Badge 
            className={`${
              bout.type === 'main' 
                ? 'bg-gradient-to-r from-red-600 to-yellow-500 text-white border-2 border-yellow-400 shadow-[0_0_20px_rgba(239,68,68,0.6)]' 
                : 'bg-gradient-to-r from-blue-600 to-purple-500 text-white border-2 border-purple-400'
            } ${compact ? 'text-xs px-3 py-1 font-bold' : 'text-lg px-6 py-2 font-black'} uppercase tracking-wider`}
          >
            {badgeText}
          </Badge>
        )}
        
        {hasTitle && (
          <div className={`flex items-center justify-center ${compact ? 'gap-2' : 'gap-4'} text-yellow-500`}>
            {isMainEvent && <Trophy className={compact ? 'w-4 h-4' : 'w-8 h-8'} weight="fill" />}
            <p className={`font-display uppercase ${
              compact 
                ? isMainEvent ? 'text-base' : 'text-sm'
                : isMainEvent ? 'text-4xl md:text-5xl' : 'text-3xl md:text-4xl'
            } tracking-[0.3em] text-center font-black drop-shadow-[0_4px_16px_rgba(234,179,8,0.8)] bg-gradient-to-r from-yellow-400 to-red-500 bg-clip-text text-transparent`}>
              {bout.title}
            </p>
            {isMainEvent && <Trophy className={compact ? 'w-4 h-4' : 'w-8 h-8'} weight="fill" />}
          </div>
        )}

        <div className={`flex items-center justify-between ${compact ? 'gap-2' : 'gap-8'} w-full`}>
          <div className={`flex-1 flex flex-col items-end ${compact ? 'gap-1' : 'gap-3'}`}>
            {bout.fighter1Image && showImages && (
              <div className="flex flex-col items-center gap-1">
                <div className={`${compact ? 'w-16 h-16' : 'w-32 h-32 md:w-40 md:h-40'} rounded-lg overflow-hidden border-2 border-primary/50 shadow-lg relative`}>
                  {bout.fighter1Rank && (
                    <div className={`absolute top-0.5 right-0.5 ${compact ? 'w-5 h-5' : 'w-10 h-10'} bg-secondary/95 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-foreground/20 shadow-lg z-10`}>
                      <span className={`font-display font-bold ${compact ? 'text-[9px]' : 'text-sm'} text-secondary-foreground`}>#{bout.fighter1Rank}</span>
                    </div>
                  )}
                  <img 
                    src={bout.fighter1Image} 
                    alt={bout.fighter1 || 'Fighter 1'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                {bout.fighter1Record && (bout.fighter1Record.wins || bout.fighter1Record.losses || bout.fighter1Record.knockouts) && (
                  <div className={`flex items-center gap-1 ${compact ? 'text-[9px]' : 'text-sm'} font-semibold text-muted-foreground`}>
                    <span className="text-foreground">{bout.fighter1Record.wins || '0'}</span>
                    <span>|</span>
                    <span className="text-foreground">{bout.fighter1Record.losses || '0'}</span>
                    <span>|</span>
                    <span className="text-foreground">{bout.fighter1Record.knockouts || '0'}</span>
                  </div>
                )}
                {odds && (
                  <Badge variant="outline" className={`${compact ? 'text-[9px] px-1.5 py-0.5' : 'text-xs px-2 py-1'} font-mono font-bold`}>
                    {formatOdds(odds.fighter1Odds, 'american')}
                  </Badge>
                )}
              </div>
            )}
            <p className={`font-fighter uppercase ${nameSize} leading-none tracking-tight text-foreground text-right font-black drop-shadow-[0_6px_20px_rgba(0,0,0,0.8)] hover:scale-105 transition-transform break-words`}>
              {bout.fighter1 || 'TBD'}
            </p>
            {odds && !bout.fighter1Image && (
              <Badge variant="outline" className={`${compact ? 'text-xs px-2 py-1' : 'text-lg px-4 py-2'} font-mono font-bold bg-primary/10 border-primary/30`}>
                {formatOdds(odds.fighter1Odds, 'american')}
              </Badge>
            )}
          </div>
          
          <div className={`flex flex-col items-center justify-center ${compact ? 'px-1.5' : 'px-6'}`}>
            <p className={`font-display ${
              compact 
                ? isMainEvent ? 'text-3xl' : 'text-2xl'
                : isMainEvent ? 'text-8xl md:text-9xl' : 'text-6xl md:text-7xl'
            } text-red-500 font-black drop-shadow-[0_0_30px_rgba(239,68,68,0.8)] animate-pulse`}>VS</p>
          </div>
          
          <div className={`flex-1 flex flex-col items-start ${compact ? 'gap-1' : 'gap-3'}`}>
            {bout.fighter2Image && showImages && (
              <div className="flex flex-col items-center gap-1">
                <div className={`${compact ? 'w-16 h-16' : 'w-32 h-32 md:w-40 md:h-40'} rounded-lg overflow-hidden border-2 border-primary/50 shadow-lg relative`}>
                  {bout.fighter2Rank && (
                    <div className={`absolute top-0.5 left-0.5 ${compact ? 'w-5 h-5' : 'w-10 h-10'} bg-secondary/95 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-foreground/20 shadow-lg z-10`}>
                      <span className={`font-display font-bold ${compact ? 'text-[9px]' : 'text-sm'} text-secondary-foreground`}>#{bout.fighter2Rank}</span>
                    </div>
                  )}
                  <img 
                    src={bout.fighter2Image} 
                    alt={bout.fighter2 || 'Fighter 2'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                {bout.fighter2Record && (bout.fighter2Record.wins || bout.fighter2Record.losses || bout.fighter2Record.knockouts) && (
                  <div className={`flex items-center gap-1 ${compact ? 'text-[9px]' : 'text-sm'} font-semibold text-muted-foreground`}>
                    <span className="text-foreground">{bout.fighter2Record.wins || '0'}</span>
                    <span>|</span>
                    <span className="text-foreground">{bout.fighter2Record.losses || '0'}</span>
                    <span>|</span>
                    <span className="text-foreground">{bout.fighter2Record.knockouts || '0'}</span>
                  </div>
                )}
                {odds && (
                  <Badge variant="outline" className={`${compact ? 'text-[9px] px-1.5 py-0.5' : 'text-xs px-2 py-1'} font-mono font-bold`}>
                    {formatOdds(odds.fighter2Odds, 'american')}
                  </Badge>
                )}
              </div>
            )}
            <p className={`font-fighter uppercase ${nameSize} leading-none tracking-tight text-foreground text-left font-black drop-shadow-[0_6px_20px_rgba(0,0,0,0.8)] hover:scale-105 transition-transform break-words`}>
              {bout.fighter2 || 'TBD'}
            </p>
            {odds && !bout.fighter2Image && (
              <Badge variant="outline" className={`${compact ? 'text-xs px-2 py-1' : 'text-lg px-4 py-2'} font-mono font-bold bg-primary/10 border-primary/30`}>
                {formatOdds(odds.fighter2Odds, 'american')}
              </Badge>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card 
      ref={cardRef}
      onContextMenu={handleContextMenu}
      className={`w-full ${compact ? 'max-w-full' : 'max-w-6xl'} mx-auto overflow-hidden border-4 border-yellow-500/50 shadow-[0_0_50px_rgba(234,179,8,0.3)] relative cursor-context-menu`}
      title="Right-click to save as image"
    >
      {fightCard.backgroundImage && (
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${fightCard.backgroundImage})` }}
        />
      )}
      <div className={`relative ${fightCard.backgroundImage ? 'bg-card/95 backdrop-blur-sm' : 'bg-gradient-to-br from-card via-card to-muted'} ${compact ? 'p-3 flex flex-col gap-2.5' : 'p-8 md:p-12 flex flex-col gap-8'}`}>
        <div className="text-center space-y-1">
          {fightCard.customLogo ? (
            <div className="flex justify-center">
              <img 
                src={fightCard.customLogo} 
                alt="LSBA Logo" 
                className={`${compact ? 'max-h-12' : 'max-h-32 md:max-h-40'} object-contain drop-shadow-2xl`}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          ) : (
            <>
              <h1 className={`font-display ${compact ? 'text-2xl' : 'text-7xl md:text-9xl'} uppercase tracking-[0.2em] font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-red-500 to-yellow-400 drop-shadow-[0_0_25px_rgba(234,179,8,0.5)] relative`}>
                <span className="absolute inset-0 text-yellow-500/30 blur-2xl scale-110">LOS SANTOS BOXING ASSOCIATION</span>
                <span className="relative drop-shadow-[0_4px_20px_rgba(239,68,68,0.8)]">LOS SANTOS BOXING ASSOCIATION</span>
              </h1>
              {!compact && (
                <p className={`text-lg md:text-2xl text-muted-foreground/80 font-bold tracking-[0.4em] uppercase mt-2`}>
                  Professional Boxing Commission
                </p>
              )}
            </>
          )}
        </div>

        <Separator className="bg-primary/50" />

        {renderBout(fightCard.mainEvent, true, 'MAIN EVENT')}

        {fightCard.coMainEvent && fightCard.coMainEvent.fighter1 && fightCard.coMainEvent.fighter2 && (
          <>
            <Separator className="bg-accent/30" />
            {renderBout(fightCard.coMainEvent, true, 'CO-MAIN EVENT')}
          </>
        )}

        {fightCard.otherBouts.length > 0 && fightCard.otherBouts.some(b => b.fighter1 || b.fighter2) && (
          <>
            <Separator className="bg-muted-foreground/20" />
            <div className={`flex flex-col ${compact ? 'gap-2' : 'gap-6'}`}>
              <h3 className={`font-display ${compact ? 'text-sm' : 'text-3xl md:text-4xl'} uppercase text-center text-secondary tracking-[0.2em] font-extrabold drop-shadow-md`}>
                Fight Card
              </h3>
              {fightCard.otherBouts.map((bout) => {
                if (!bout.fighter1 && !bout.fighter2) return null;
                const boutNameSize = getFighterNameSize(bout.fighter1 || '', bout.fighter2 || '', false, false, compact);
                return (
                  <div key={bout.id} className="flex flex-col items-center gap-1">
                    {bout.title && (
                      <p className={`${compact ? 'text-[9px]' : 'text-sm'} text-muted-foreground uppercase tracking-widest font-semibold`}>
                        {bout.title}
                      </p>
                    )}
                    <div className={`flex items-center justify-center ${compact ? 'gap-1.5' : 'gap-4'} flex-wrap max-w-full`}>
                      <p className={`font-fighter ${boutNameSize} uppercase text-foreground font-bold drop-shadow-md break-words text-center`}>
                        {bout.fighter1 || 'TBD'}
                      </p>
                      <span className={`text-primary font-display ${compact ? 'text-xs' : 'text-2xl md:text-3xl'} font-bold flex-shrink-0`}>VS</span>
                      <p className={`font-fighter ${boutNameSize} uppercase text-foreground font-bold drop-shadow-md break-words text-center`}>
                        {bout.fighter2 || 'TBD'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <Separator className="bg-primary/50" />

        <div className={`flex flex-col md:flex-row justify-between items-center ${compact ? 'gap-2' : 'gap-6'} text-center md:text-left bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 ${compact ? 'p-2' : 'p-4'} rounded-lg border border-primary/20`}>
          <div>
            <p className={`${compact ? 'text-xs' : 'text-sm'} uppercase text-yellow-500 tracking-widest mb-1 font-bold`}>📅 DATE</p>
            <p className={`font-bold text-foreground ${compact ? 'text-sm' : 'text-xl'}`}>
              {fightCard.eventDate || 'TBD'}
            </p>
          </div>
          <div>
            <p className={`${compact ? 'text-xs' : 'text-sm'} uppercase text-yellow-500 tracking-widest mb-1 font-bold`}>📍 LOCATION</p>
            <p className={`font-bold text-foreground ${compact ? 'text-sm' : 'text-xl'} uppercase`}>
              {fightCard.location || 'TBD'}
            </p>
          </div>
        </div>

        {sponsors.length > 0 && (
          <>
            <Separator className="bg-muted-foreground/20" />
            <div className="text-center">
              <p className={`${compact ? 'text-[9px]' : 'text-xs'} uppercase text-muted-foreground tracking-widest mb-1`}>
                Sponsored By
              </p>
              <div className={`flex flex-wrap justify-center ${compact ? 'gap-1' : 'gap-3'}`}>
                {sponsors.map((sponsor, index) => (
                  <span
                    key={index}
                    className={`${compact ? 'text-[9px] px-2 py-0.5' : 'text-sm px-3 py-1'} font-medium text-secondary border border-secondary/30 rounded`}
                  >
                    {sponsor}
                  </span>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
